import RadioButton from "components/radio-button";
import { useCallback, useMemo, useRef, useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import QRCode from "react-qr-code";
import { AmplitudePageName, track } from "utils/amplitude";

import { ReactComponent as NoteCopied } from "assets/svg/note-copied.svg";
import { ReactComponent as OutlineArrowLeft16 } from "assets/svg/outline-arrow-left-16.svg";
import { ReactComponent as OutlineCopy } from "assets/svg/outline-copy-primary-24.svg";
import { ReactComponent as OutlineEyeClosed } from "assets/svg/outline-eye-closed.svg";
import { ReactComponent as OutlineEyeOpen } from "assets/svg/outline-eye-open.svg";
import ConfirmModal from "components/confirm-modal";
import IconButton from "components/icon-button";
import SuccessTag from "components/success-tag/SuccessTag";
import { useNavigate } from "react-router-dom";
import setupMFA from "services/mfa/setupMFA";
import verifyAndEnableMFA from "services/mfa/verifyAndEnableMFA";

import { PASSWORD_PATTERN } from "constants/passwordPattern";
import { SHOW_MFA_ALL_SET_MODAL } from "constants/showMFAAllSetModal";
import OtpInput from "./otp-input/OtpInput";

import getClasses from "utils/getClasses";
import styles from "./MFA.module.scss";

const MFA_STEPS = ["chose-text-or-app", "scan-qr-or-copy-secret", "paste-code"];
const AMPLITUDE_PAGE_NAME: AmplitudePageName = "mfa-setup";

interface MfaSetupData {
  otpauth_url: string;
  secret_code: string;
}

const MFA = () => {
  const navigate = useNavigate();
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  const [copied, setCopied] = useState(false);
  const [currentStep, setCurrentStep] = useState(MFA_STEPS[0]);
  const [selectedOption, setSelectedOption] = useState("app");
  const [securityCode, setSecurityCode] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const [showEnterPassword, setShowEnterPassword] = useState(true);
  const [password, setPassword] = useState("");
  const [revealPassword, setRevealPassword] = useState(false);

  const [error, setError] = useState<any>();

  const [mfaSetupData, setMfaSetupData] = useState<MfaSetupData>();

  const currentIndex = MFA_STEPS.indexOf(currentStep);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === MFA_STEPS.length - 1;

  const handleOptionChange = (value) => {
    setSelectedOption(value);
  };

  const handleNext = () => {
    const currentIndex = MFA_STEPS.indexOf(currentStep);
    if (currentIndex < MFA_STEPS.length - 1) {
      setCurrentStep(MFA_STEPS[currentIndex + 1]);
    }
  };

  const handleBack = () => {
    const currentIndex = MFA_STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(MFA_STEPS[currentIndex - 1]);
    }
  };

  const isCurrentStepInvalid = useMemo(() => {
    // Add validation logic for each step
    switch (currentStep) {
      case MFA_STEPS[0]: // "chose-text-or-app"
        // Add validation for MFA setup
        return !selectedOption;
      case MFA_STEPS[1]: // "scan-qr-or-copy-secret"
        return false;
      case MFA_STEPS[2]: // "paste-code"
        return securityCode.length !== 6;
      // Add validation for completion
      default:
        return false;
    }
  }, [currentStep, selectedOption, securityCode]);

  const submitStep = useCallback(async () => {
    setIsBusy(true);
    try {
      // Add logic to save current step data
    } catch (error) {
      // Handle error
    } finally {
      setIsBusy(false);
    }
  }, []);

  const handlePasswordConfirm = async () => {
    if (password) {
      try {
        const res = await setupMFA(password);
        setMfaSetupData(res);
        setShowEnterPassword(false);
      } catch (error) {
        setError(error);
      }
    } else {
      setError("Password is required");
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const verifyOtp = async () => {
    setIsBusy(true);
    try {
      await verifyAndEnableMFA(securityCode);
      window.localStorage.setItem(
        SHOW_MFA_ALL_SET_MODAL.key,
        SHOW_MFA_ALL_SET_MODAL.value,
      );
      window.location.assign(
        `${window.location.origin}/pro-dashboard/settings/account`,
      );
    } catch (error) {
      setError(error);
    } finally {
      setIsBusy(false);
    }
  };

  const togglePassword = (event) => {
    event.preventDefault();
    setRevealPassword(!revealPassword);
  };

  const handlePasswordKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      handlePasswordConfirm();
    }
  };

  if (showEnterPassword) {
    return (
      <ConfirmModal
        labelTitle={"Enter password"}
        children={
          <>
            <p>
              Enter your password to continue setting up 2-step verification.
            </p>
            <Form.Group
              className={styles["form-group"]}
              controlId="mfaPassword"
            >
              <Form.Label className={styles["form-label"]}>Password</Form.Label>
              <InputGroup
                className={getClasses(
                  "align-items-center mb-2",
                  styles["password"],
                )}
              >
                <Form.Control
                  required
                  name="password"
                  pattern={PASSWORD_PATTERN.source}
                  type={revealPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handlePasswordKeyDown}
                  autoComplete="current-password"
                />
                <InputGroup.Text
                  onClick={togglePassword}
                  style={{ cursor: "pointer" }}
                >
                  {revealPassword ? (
                    <OutlineEyeOpen className={"gray01"} />
                  ) : (
                    <OutlineEyeClosed className={"gray01"} />
                  )}
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </>
        }
        labelCancel={"Cancel"}
        labelConfirm={"Continue"}
        show={showEnterPassword}
        onCancel={() => {
          navigate("/pro-dashboard/settings/account");
        }}
        onConfirm={handlePasswordConfirm}
        error={error}
      />
    );
  }

  return (
    <div className={styles["mfa"]}>
      <div className={styles["step-bar"]}>
        {MFA_STEPS.map((step, index) => (
          <div
            key={step}
            className={getClasses(
              styles["step-bar-item"],
              currentIndex >= index && styles["step-bar-item--active"],
            )}
          />
        ))}
      </div>
      <div className={styles["steps"]}>
        <div
          className={getClasses(
            styles["step-content"],
            currentStep !== MFA_STEPS[0] && styles.hidden,
          )}
        >
          <h1>How would you like to verify your sign-in?</h1>
          <p>Choose how you'd like to receive your security code.</p>
          <div className={styles["radio-group"]}>
            <div
              className={getClasses(
                styles["custom-radio"],
                selectedOption === "app" && styles["custom-radio--active"],
              )}
            >
              <RadioButton
                aria-label="Authentication app"
                name="verification-method"
                label={
                  <div className={styles["radio-group-item"]}>
                    <span className={styles["radio-group-item__title"]}>
                      Authentication app
                    </span>
                    <span className={styles["radio-group-item__description"]}>
                      Use an app like{" "}
                      <a
                        href="https://www.authy.com/download/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["radio-group-item__link"]}
                      >
                        Authy
                      </a>
                      ,{" "}
                      <a
                        href="https://apps.apple.com/us/app/duo-mobile/id422663827"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["radio-group-item__link"]}
                      >
                        Duo
                      </a>
                      , or{" "}
                      <a
                        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles["radio-group-item__link"]}
                      >
                        Google Authenticator
                      </a>{" "}
                      to generate codes.
                    </span>
                  </div>
                }
                checked={selectedOption === "app"}
                onChange={() => handleOptionChange("app")}
              />
            </div>
            <div
              className={getClasses(
                styles["custom-radio"],
                selectedOption === "sms" && styles["custom-radio--active"],
              )}
            >
              <RadioButton
                aria-label="Text message"
                name="verification-method"
                label={
                  <div className={styles["radio-group-item"]}>
                    <span className={styles["radio-group-item__title"]}>
                      Text message
                    </span>
                    <span className={styles["radio-group-item__description"]}>
                      Receive a code via SMS
                    </span>
                  </div>
                }
                checked={selectedOption === "sms"}
                onChange={() => handleOptionChange("sms")}
                disabled
              />
            </div>
          </div>
        </div>

        <div
          className={getClasses(
            styles["step-content"],
            currentStep !== MFA_STEPS[1] && styles.hidden,
          )}
        >
          <h1>Register Orchid in your authentication app</h1>
          <p>
            If you don't have one, try{" "}
            <a
              href="https://www.authy.com/download/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles["step-content__link"]}
            >
              Authy
            </a>
            ,{" "}
            <a
              href="https://apps.apple.com/us/app/duo-mobile/id422663827"
              target="_blank"
              rel="noopener noreferrer"
              className={styles["step-content__link"]}
            >
              Duo
            </a>
            , or{" "}
            <a
              href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_US"
              target="_blank"
              rel="noopener noreferrer"
              className={styles["step-content__link"]}
            >
              Google Authenticator
            </a>
            .
          </p>
          <div className={styles["qr-code-placeholder"]}>
            <div>Use your authentication app to scan this QR code:</div>
            <QRCode value={mfaSetupData?.otpauth_url ?? ""} size={200} />
            <div>
              <div className={styles["qr-code-placeholder__or"]}>Or</div>{" "}
              <div className={styles["qr-code-placeholder__enter-code"]}>
                manually enter this code:{" "}
              </div>
            </div>
            <div className={styles["qr-code-placeholder__code"]}>
              <Form.Control
                type="text"
                readOnly
                value={
                  mfaSetupData?.secret_code
                    ? mfaSetupData.secret_code.replace(/(.{4})/g, "$1 ")
                    : "TEST_SECRET_CODE"
                }
                onFocus={(e) => e.target.select()}
                className={styles["qr-code-placeholder__code-text"]}
              />
              <IconButton
                ref={copyButtonRef}
                onClick={() => {
                  copyToClipboard(mfaSetupData?.secret_code || "");
                }}
              >
                {!copied ? <OutlineCopy /> : <NoteCopied />}
              </IconButton>
            </div>
          </div>
        </div>

        {copied && (
          <SuccessTag
            target={copyButtonRef.current}
            show={copied}
            onHide={() => setCopied(false)}
            placement={"top"}
            successMsg={"Copied"}
            duration={1000}
          />
        )}

        <div
          className={getClasses(
            styles["step-content"],
            currentStep !== MFA_STEPS[2] && styles.hidden,
          )}
        >
          <h1>
            Enter the 6-digit security codes from your authentication app.
          </h1>
          <div className={styles["otp-input-container"]}>
            <div>
              <OtpInput
                value={securityCode}
                onChange={setSecurityCode}
                error={error?.toString()}
              />
            </div>
            <div>It will expire shortly, so enter soon</div>
          </div>
        </div>

        <div className={styles.navigation}>
          {!isFirstStep && (
            <Button
              variant="outline-primary"
              onClick={() => {
                track(AMPLITUDE_PAGE_NAME, ["back"], "click");
                handleBack();
              }}
              disabled={isBusy}
              className={styles["navigation__back-button"]}
            >
              <OutlineArrowLeft16 />
              Back
            </Button>
          )}
          {isLastStep ? (
            <Button
              variant="primary"
              onClick={() => {
                track(AMPLITUDE_PAGE_NAME, ["finish"], "click");
                verifyOtp();
              }}
              disabled={isCurrentStepInvalid || isBusy}
            >
              {isBusy ? "Please wait..." : "Verify"}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                track(AMPLITUDE_PAGE_NAME, ["continue"], "click");
                submitStep();
                handleNext();
              }}
              disabled={isCurrentStepInvalid}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFA;
